import {
  formatUsPhone,
  normalizeUsPhone,
  PERMISSIONS,
  queryKeys,
  ROLE_KEYS,
  type RoleKey,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, KeyRound, Pencil, Smartphone, UserPlus, Users, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

interface StaffRow {
  id: string
  email: string
  name: string
  role: RoleKey
  isActive: boolean
  mustChangePassword: boolean
  lockedUntil: string | null
  lastLoginAt: string | null
  createdAt: string
  twoFactorChannel: 'email' | 'sms'
  twoFactorPhone: string | null
}

interface RoleRow {
  key: RoleKey
  name: string
  description: string | null
  permissions: string[]
  staffCount: number
}

const ROLE_LABEL: Record<RoleKey, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  sales: 'Sales',
  technician: 'Technician',
  support: 'Support',
  guest: 'Guest',
}

/** Domain prefix (the part before the ":") to the group heading it is shown
 *  under in the permissions dialog. Mirrors the grouping comments in
 *  `packages/shared/src/constants/permissions.ts`. */
const PERMISSION_GROUP_LABEL: Record<string, string> = {
  catalog: 'Catalog',
  inventory: 'Inventory',
  customers: 'Customers',
  orders: 'Orders',
  repairs: 'Repairs',
  kiosks: 'Kiosks',
  staff: 'Staff administration',
  roles: 'Staff administration',
  reports: 'Reporting',
  audit: 'Reporting',
  settings: 'Settings',
  integrations: 'Integrations',
  wiki: 'Wiki',
}

/** The permission catalog, grouped for display — computed once since
 *  `PERMISSIONS` is a static, build-time list. */
const PERMISSION_GROUPS: [string, string[]][] = (() => {
  const groups = new Map<string, string[]>()
  for (const permission of PERMISSIONS) {
    const prefix = permission.split(':')[0] ?? permission
    const label = PERMISSION_GROUP_LABEL[prefix] ?? prefix
    const list = groups.get(label)
    if (list) list.push(permission)
    else groups.set(label, [permission])
  }
  return [...groups.entries()]
})()

/**
 * Who can sign in, and what they can do.
 *
 * Accounts are switched off rather than deleted: every provisioning signature,
 * order and audit row points at one, and a dangling id explains nothing when
 * someone asks who checked a phone eighteen months ago.
 */
export function StaffPage() {
  const { can, staff: me } = useAuth()
  const [creating, setCreating] = useState(false)
  const [resetting, setResetting] = useState<StaffRow | null>(null)
  const [editingCodes, setEditingCodes] = useState<StaffRow | null>(null)
  const [viewingRole, setViewingRole] = useState<RoleRow | null>(null)

  const { data: people, isLoading } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
  })

  const { data: roles } = useQuery({
    queryKey: queryKeys.staff.roles(),
    queryFn: () => api.get<RoleRow[]>('/roles'),
  })

  const manage = can('staff:manage')

  return (
    <Page>
      <PageHeader
        title="Staff"
        description="Back-office accounts and what each role is allowed to reach."
        actions={
          manage ? (
            <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
              <UserPlus /> New account
            </Button>
          ) : null
        }
      />

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-foreground-muted">Loading…</div>
          ) : people?.length ? (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                <tr>
                  <th className="px-4 py-2 text-start font-medium">Name</th>
                  <th className="px-4 py-2 text-start font-medium">Role</th>
                  <th className="px-4 py-2 text-start font-medium">Security</th>
                  <th className="px-4 py-2 text-start font-medium">Last signed in</th>
                  <th className="px-4 py-2 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {people.map((person) => (
                  <StaffRowView
                    key={person.id}
                    person={person}
                    isMe={me?.id === person.id}
                    canManage={manage}
                    onReset={() => setResetting(person)}
                    onEditCodes={() => setEditingCodes(person)}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6">
              <EmptyState icon={Users} title="No accounts" description="Nobody can sign in yet." />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(roles ?? []).map((role) => (
            <button
              key={role.key}
              type="button"
              onClick={() => setViewingRole(role)}
              className="rounded-lg border border-border p-3 text-start transition-colors hover:bg-surface-sunken"
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className="font-medium">{ROLE_LABEL[role.key] ?? role.name}</p>
                <span className="tabular text-2xs text-foreground-muted">
                  {role.staffCount} {role.staffCount === 1 ? 'person' : 'people'}
                </span>
              </div>
              {role.description ? (
                <p className="mt-1 text-xs text-foreground-muted">{role.description}</p>
              ) : null}
              <p className="mt-2 text-2xs text-foreground-subtle">
                {role.permissions.length} permissions · view
              </p>
            </button>
          ))}
        </CardContent>
      </Card>

      <CreateStaffDialog open={creating} onOpenChange={setCreating} />
      <ResetPasswordDialog person={resetting} onClose={() => setResetting(null)} />
      <SignInCodesDialog person={editingCodes} onClose={() => setEditingCodes(null)} />
      <RolePermissionsDialog role={viewingRole} onClose={() => setViewingRole(null)} />
    </Page>
  )
}

function StaffRowView({
  person,
  isMe,
  canManage,
  onReset,
  onEditCodes,
}: {
  person: StaffRow
  isMe: boolean
  canManage: boolean
  onReset: () => void
  onEditCodes: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState(person.name)

  const update = useMutation({
    mutationFn: (patch: { name?: string; role?: RoleKey; isActive?: boolean }) =>
      api.patch(`/staff/${person.id}`, patch),
    onSuccess: async (_data, patch) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      if (isMe && patch.name !== undefined) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.me() })
      }
      toast.success('Saved')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  function startRename() {
    setDraftName(person.name)
    setRenaming(true)
  }

  function saveRename() {
    const name = draftName.trim()
    if (name && name !== person.name) {
      update.mutate({ name }, { onSuccess: () => setRenaming(false) })
    } else {
      setRenaming(false)
    }
  }

  const isLocked = person.lockedUntil !== null && new Date(person.lockedUntil) > new Date()

  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-2.5">
        {renaming ? (
          <div className="flex items-center gap-1">
            <Input
              autoFocus
              value={draftName}
              maxLength={150}
              aria-label={`Name for ${person.email}`}
              className="h-7 w-44 text-sm"
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') saveRename()
                if (event.key === 'Escape') setRenaming(false)
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={saveRename}
              disabled={update.isPending}
              title="Save name"
            >
              <Check />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setRenaming(false)} title="Cancel">
              <X />
            </Button>
          </div>
        ) : (
          <p className="font-medium">
            {person.name}
            {isMe ? <span className="ms-1.5 text-2xs text-foreground-subtle">(you)</span> : null}
            {canManage ? (
              <button
                type="button"
                className="ms-1.5 align-middle text-foreground-subtle hover:text-foreground"
                onClick={startRename}
                title="Edit name"
                aria-label={`Edit name for ${person.email}`}
              >
                <Pencil className="size-3" />
              </button>
            ) : null}
          </p>
        )}
        <p className="text-2xs text-foreground-muted">{person.email}</p>
      </td>

      <td className="px-4 py-2.5">
        {canManage && !isMe ? (
          <Select
            value={person.role}
            onValueChange={(role) => update.mutate({ role: role as RoleKey })}
          >
            <SelectTrigger className="h-7 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {ROLE_LABEL[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-foreground-muted">{ROLE_LABEL[person.role]}</span>
        )}
      </td>

      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-1">
          {!person.isActive ? <Badge tone="danger">Disabled</Badge> : null}
          {isLocked ? <Badge tone="warning">Locked out</Badge> : null}
          {person.mustChangePassword ? <Badge tone="info">Must change password</Badge> : null}
        </div>
        <p className="mt-1 text-2xs text-foreground-muted">
          {person.twoFactorChannel === 'sms' && person.twoFactorPhone
            ? `Sign-in codes by text · ${formatUsPhone(person.twoFactorPhone)}`
            : 'Sign-in codes by email'}
        </p>
      </td>

      <td className="px-4 py-2.5 text-2xs text-foreground-muted">
        {person.lastLoginAt
          ? new Date(person.lastLoginAt).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })
          : 'Never'}
      </td>

      <td className="px-4 py-2.5 text-end">
        {canManage ? (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={onReset} title="Set a new password">
              <KeyRound /> Password
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditCodes}
              title="Choose how sign-in codes are sent"
            >
              <Smartphone /> Codes
            </Button>
            {!isMe ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update.mutate({ isActive: !person.isActive })}
              >
                {person.isActive ? 'Disable' : 'Enable'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </td>
    </tr>
  )
}

/**
 * A generated passphrase rather than a box someone types "Welcome123" into.
 *
 * It is shown once, handed over in person, and the account is forced to change
 * it at first sign-in — so it only has to survive the walk to the next desk.
 */
function generatePassphrase(): string {
  const words = [
    'anchor',
    'basket',
    'candle',
    'copper',
    'ember',
    'garden',
    'harbor',
    'ivory',
    'jasmine',
    'kettle',
    'lantern',
    'marble',
    'nickel',
    'orchard',
    'pebble',
    'quarry',
    'ribbon',
    'saddle',
    'timber',
    'velvet',
    'willow',
    'zephyr',
  ]
  const pick = () => {
    const bytes = new Uint32Array(1)
    crypto.getRandomValues(bytes)
    return words[(bytes[0] ?? 0) % words.length]
  }
  const digits = new Uint32Array(1)
  crypto.getRandomValues(digits)
  return `${pick()}-${pick()}-${pick()}-${(digits[0] ?? 0) % 100}`
}

function CreateStaffDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<RoleKey>('sales')
  const [password, setPassword] = useState(generatePassphrase)
  const [created, setCreated] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.post('/staff', { email, name, role, temporaryPassword: password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      setCreated(password)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const close = () => {
    onOpenChange(false)
    setEmail('')
    setName('')
    setRole('sales')
    setPassword(generatePassphrase())
    setCreated(null)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{created ? 'Account created' : 'New account'}</DialogTitle>
        </DialogHeader>

        {created ? (
          <DialogBody className="space-y-4">
            <Alert tone="success">
              Give {name} this password in person. It is not shown again, and they will be asked to
              change it the first time they sign in.
            </Alert>
            <div className="rounded-lg border border-border bg-surface-sunken p-3">
              <p className="font-identifier text-lg">{created}</p>
            </div>
            <p className="text-xs text-foreground-muted">
              They will be emailed a one-time code each time they sign in. Once the account exists,
              you can switch them to text-message codes with the Codes button.
            </p>
          </DialogBody>
        ) : (
          <DialogBody className="space-y-4">
            <Field label="Name" required>
              {(props) => (
                <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
            <Field label="Email" required>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Role" required>
              {(props) => (
                <Select value={role} onValueChange={(v) => setRole(v as RoleKey)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_KEYS.map((key) => (
                      <SelectItem key={key} value={key}>
                        {ROLE_LABEL[key]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field
              label="Temporary password"
              hint="Generated for you. They must change it at first sign-in."
            >
              {(props) => (
                <div className="flex gap-2">
                  <Input
                    {...props}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => setPassword(generatePassphrase())}
                  >
                    New
                  </Button>
                </div>
              )}
            </Field>
          </DialogBody>
        )}

        <DialogFooter>
          {created ? (
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => create.mutate()}
                disabled={create.isPending || !name || !email}
              >
                {create.isPending ? 'Creating…' : 'Create account'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SignInCodesDialog({ person, onClose }: { person: StaffRow | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [channel, setChannel] = useState<'email' | 'sms'>('email')
  const [phone, setPhone] = useState('')

  // Reset the form to the account's saved values each time a different
  // account is opened.
  useEffect(() => {
    if (!person) return
    setChannel(person.twoFactorChannel)
    setPhone(person.twoFactorPhone ? formatUsPhone(person.twoFactorPhone) : '')
  }, [person])

  const normalized = phone.trim() ? normalizeUsPhone(phone) : null
  const phoneInvalid = phone.trim() !== '' && normalized === null
  const missingPhone = channel === 'sms' && normalized === null

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/staff/${person?.id}`, {
        twoFactorChannel: channel,
        twoFactorPhone: normalized,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  return (
    <Dialog open={person !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sign-in codes for {person?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Send the code by">
            {(props) => (
              <Select value={channel} onValueChange={(v) => setChannel(v as 'email' | 'sms')}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email ({person?.email})</SelectItem>
                  <SelectItem value="sms">Text message</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field
            label="Mobile number"
            hint="US number the text is sent to. Kept even if you switch back to email."
            error={phoneInvalid ? 'Enter a valid US phone number' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={phone}
                placeholder="(732) 555-0142"
                onChange={(e) => setPhone(e.target.value)}
                invalid={phoneInvalid}
              />
            )}
          </Field>
          <p className="text-xs text-foreground-muted">
            If a text can't be sent, the code is emailed instead, and at sign-in they can always ask
            for it by email.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={phoneInvalid || missingPhone}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ResetPasswordDialog({
  person,
  onClose,
}: {
  person: StaffRow | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [password, setPassword] = useState(generatePassphrase)
  const [done, setDone] = useState(false)

  const reset = useMutation({
    mutationFn: () =>
      api.post(`/staff/${person?.id}/reset-password`, { temporaryPassword: password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      setDone(true)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const close = () => {
    onClose()
    setPassword(generatePassphrase())
    setDone(false)
  }

  return (
    <Dialog open={person !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New password for {person?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {done ? (
            <>
              <Alert tone="success">
                Done. Every session they had is now signed out, and they will be asked to change
                this the first time they use it.
              </Alert>
              <div className="rounded-lg border border-border bg-surface-sunken p-3">
                <p className="font-identifier text-lg">{password}</p>
              </div>
            </>
          ) : (
            <>
              <Alert tone="warning">This signs them out everywhere and clears any lockout.</Alert>
              <Field label="Temporary password">
                {(props) => (
                  <div className="flex gap-2">
                    <Input
                      {...props}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setPassword(generatePassphrase())}
                    >
                      New
                    </Button>
                  </div>
                )}
              </Field>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          {done ? (
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => reset.mutate()} disabled={reset.isPending}>
                {reset.isPending ? 'Resetting…' : 'Reset password'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RolePermissionsDialog({ role, onClose }: { role: RoleRow | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = useAuth()
  const [checked, setChecked] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (role) setChecked(new Set(role.permissions))
  }, [role])

  const editable = can('roles:manage') && role?.key !== 'admin'
  const dirty = role
    ? checked.size !== role.permissions.length || role.permissions.some((p) => !checked.has(p))
    : false

  const save = useMutation({
    mutationFn: () => api.patch(`/roles/${role?.key}`, { permissions: [...checked].sort() }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.me() }),
      ])
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const toggle = (permission: string, isChecked: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (isChecked) next.add(permission)
      else next.delete(permission)
      return next
    })
  }

  return (
    <Dialog open={role !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Permissions — {role ? (ROLE_LABEL[role.key] ?? role.name) : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {role?.key === 'admin' ? (
            <Alert tone="info">
              The administrator role always has every permission and cannot be edited here.
            </Alert>
          ) : !editable ? (
            <Alert tone="info">
              You do not have permission to change this. Showing it read-only.
            </Alert>
          ) : null}
          <div className="space-y-4">
            {PERMISSION_GROUPS.map(([group, permissions]) => (
              <div key={group}>
                <p className="text-2xs font-medium uppercase tracking-wide text-foreground-subtle">
                  {group}
                </p>
                <div className="mt-1 space-y-1">
                  {permissions.map((permission) => (
                    <label key={permission} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={checked.has(permission)}
                        disabled={!editable}
                        onChange={(e) => toggle(permission, e.target.checked)}
                      />
                      {permission}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogFooter>
          {editable ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => save.mutate()}
                disabled={save.isPending || !dirty}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
